import LoadingSpinner from '@common/src/components/LoadingSpinner';
import { useAnvaFlowsCompanies } from '@flows/src/api/anva-flows/getAnvaFlowsCompanies';
import FlowsConfigureCompanyItem from '@flows/src/components/flows-configure/insurances/FlowsConfigureCompanyItem';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import useAnvaInsuranceName from '@flows/src/hooks/flows-configure/useAnvaInsuranceGroupName';
import { useFlowsConfigureParams } from '@flows/src/hooks/flows-configure/useFlowsConfigureParams';
import useFlowsConfigureSelectAllButtonProps from '@flows/src/hooks/flows-configure/useFlowsConfigureSelectAllButtonProps';
import { useFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/flowsConfigureStore';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';

const FlowsConfigureCompanies: FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | undefined>(undefined);
  const { flowId, insuranceId: insuranceGroupId } = useFlowsConfigureParams();
  const { handleDeselectAll, handleSelectAll } =
    useFlowsConfigureSelectAllButtonProps('companies', insuranceGroupId);
  const insuranceGroups = useFlowsConfigureStore((state) => state.insurances);
  const insuranceGroup = insuranceGroups.find(
    (group) => group.id === Number(insuranceGroupId),
  );
  const { data: companies, isLoading } = useAnvaFlowsCompanies(
    insuranceGroup ? insuranceGroup.insurance_ids : undefined,
    insuranceGroupId
      ? useFlowsConfigureStore.getState().coverages[insuranceGroupId]
      : undefined,
  );
  const isAllSelected = useFlowsConfigureStore(
    (state) =>
      state.companies[insuranceGroupId || '']?.length ===
      companies?.items.length,
  );
  const { insuranceName } = useAnvaInsuranceName(insuranceGroupId || '');
  const history = useHistory();

  if (
    !insuranceGroupId ||
    !insuranceGroups.map(({ id }) => id).includes(Number(insuranceGroupId))
  ) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const currentIndex = insuranceGroups
    .map(({ id }) => id)
    .indexOf(Number(insuranceGroupId));
  const nextInsurance = insuranceGroups[currentIndex + 1]?.id;

  const handlePrevious = () => {
    history.goBack();
  };
  const handleNext = () => {
    const areCompaniesSelected =
      useFlowsConfigureStore.getState().companies?.[insuranceGroupId]?.length >
      0;
    if (!areCompaniesSelected) {
      setError(t('Select at least one option'));
      return;
    }

    if (nextInsurance) {
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${nextInsurance}/coverages`,
        search: window.location.search,
      });
    } else {
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/finish`,
        search: window.location.search,
      });
    }
  };

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('Insurance companies for ') + insuranceName}
      error={error}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <ConfigureItemsGrid singleColumn={companies?.items.length === 1}>
          {companies?.items && companies?.items.length > 1 && (
            <SelectAllButton
              isAllSelected={isAllSelected}
              onDeselectAll={handleDeselectAll}
              onSelectAll={() =>
                handleSelectAll(
                  companies?.items.map((company) => company.id) || [],
                )
              }
            />
          )}
          {companies?.items?.map((company) => (
            <FlowsConfigureCompanyItem
              key={company.id}
              insuranceId={insuranceGroupId}
              company={company}
              isError={!!error}
              clearError={() => setError(undefined)}
            />
          ))}
        </ConfigureItemsGrid>
      )}
    </ContentWrapper>
  );
};

export default FlowsConfigureCompanies;
